// File: src/components/DocumentTemplates/COI.js
import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import DocumentLayout from "../DocumentLayout";

const COI = () => {
    const [companyName, setCompanyName] = useState("Company Name Inc.");
    const [incorporationDate, setIncorporationDate] = useState("2023-01-01");
    const [incorporationNumber, setIncorporationNumber] = useState("INC123456789");
    const [jurisdiction, setJurisdiction] = useState("State of Delaware");
    const [registeredAgent, setRegisteredAgent] = useState("Registered Agent Services Inc.");
    const [selectedTemplate, setSelectedTemplate] = useState("1");

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `${companyName}-COI`,
    });

    // ---- Different Template Layouts ----
    const renderTemplate = () => {
        switch (selectedTemplate) {
            case "1": // Classic Official
                return (
                    <div ref={componentRef} className="bg-white p-10 shadow-lg border-4 border-gray-700 max-w-3xl mx-auto">
                        <h2 className="text-2xl font-bold text-center mb-2 underline">
                            CERTIFICATE OF INCORPORATION
                        </h2>
                        <p className="text-center text-gray-700 mb-6">OF</p>
                        <h1 className="text-3xl font-extrabold text-blue-800 text-center mb-8 uppercase">
                            {companyName}
                        </h1>

                        <p className="text-gray-800 leading-relaxed mb-6">
                            This is to certify that the above-named corporation was duly
                            incorporated on <b>{new Date(incorporationDate).toDateString()}</b>{" "}
                            under the incorporation number <b>{incorporationNumber}</b> in the
                            jurisdiction of <b>{jurisdiction}</b>.
                        </p>

                        <p className="mb-6">
                            The Registered Agent of the corporation is:{" "}
                            <b>{registeredAgent}</b>.
                        </p>

                        <div className="mt-12 flex justify-between items-center">
                            <div>
                                <p className="text-sm">_________________________</p>
                                <p className="text-sm">Registrar of Companies</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm">Issued on: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            case "2": // Modern Minimal
                return (
                    <div ref={componentRef} className="bg-white p-10 shadow-xl rounded-lg border max-w-3xl mx-auto">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold tracking-wide text-gray-900">
                                Certificate of Incorporation
                            </h2>
                            <p className="text-sm text-gray-600">Issued under corporate law</p>
                        </div>

                        <div className="mb-6 text-center">
                            <h1 className="text-3xl font-bold text-blue-700">{companyName}</h1>
                        </div>

                        <p className="text-gray-800 mb-4">
                            Incorporation Date: <b>{new Date(incorporationDate).toDateString()}</b>
                        </p>
                        <p className="text-gray-800 mb-4">
                            Incorporation Number: <b>{incorporationNumber}</b>
                        </p>
                        <p className="text-gray-800 mb-4">
                            Jurisdiction: <b>{jurisdiction}</b>
                        </p>
                        <p className="text-gray-800 mb-8">
                            Registered Agent: <b>{registeredAgent}</b>
                        </p>

                        <div className="border-t pt-6 flex justify-between items-center">
                            <div>
                                <p className="text-sm">Authorized Registrar</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs">Certificate Issued: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            case "3": // Decorative Header
                return (
                    <div ref={componentRef} className="bg-white p-8 shadow-lg border max-w-3xl mx-auto relative overflow-hidden">
                        {/* Decorative Header */}
                        <div className="bg-gradient-to-r from-blue-700 to-indigo-600 text-white p-6 rounded-t">
                            <h2 className="text-2xl font-bold text-center">CERTIFICATE OF INCORPORATION</h2>
                        </div>

                        <div className="p-6">
                            <h1 className="text-3xl font-bold text-center text-blue-700 mb-6">
                                {companyName}
                            </h1>
                            <p className="text-gray-700 mb-4">
                                Incorporation No: <b>{incorporationNumber}</b>
                            </p>
                            <p className="text-gray-700 mb-4">
                                Date of Incorporation: <b>{new Date(incorporationDate).toDateString()}</b>
                            </p>
                            <p className="text-gray-700 mb-4">
                                Jurisdiction: <b>{jurisdiction}</b>
                            </p>
                            <p className="text-gray-700 mb-6">
                                Registered Agent: <b>{registeredAgent}</b>
                            </p>

                            <div className="mt-12 flex justify-between">
                                <div>
                                    <p className="text-sm">_________________________</p>
                                    <p className="text-xs">Registrar</p>
                                </div>
                                <p className="text-xs">Issued on {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (

        <div className="space-y-6 bg-slate-50 p-6">
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium">Company Name</label>
                    <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Incorporation Date</label>
                    <input
                        type="date"
                        value={incorporationDate}
                        onChange={(e) => setIncorporationDate(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Incorporation Number</label>
                    <input
                        type="text"
                        value={incorporationNumber}
                        onChange={(e) => setIncorporationNumber(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Jurisdiction</label>
                    <input
                        type="text"
                        value={jurisdiction}
                        onChange={(e) => setJurisdiction(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Registered Agent</label>
                    <input
                        type="text"
                        value={registeredAgent}
                        onChange={(e) => setRegisteredAgent(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Template Style</label>
                    <select
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        className="w-full p-2 border rounded"
                    >
                        <option value="1">Classic Official</option>
                        <option value="2">Modern Minimal</option>
                        <option value="3">Decorative Header</option>
                    </select>
                </div>
            </div>
            <DocumentLayout
                title="Certificate of Incorporation"
                description="Official certificate of incorporation document"
                onSave={() => alert("COI Saved!")}
                onDownload={handlePrint}
                onPrint={handlePrint}
            >
                {/* Preview */}
                <div className="mt-8 border rounded bg-gray-50">{renderTemplate()}</div>
            </DocumentLayout>
        </div>
    );
};

export default COI;
